import { withNamespaces } from "react-i18next";
import { Badge } from "reactstrap";

import UserUtils from "src/services/utils/UserUtils";
import {
    EMPTY_VALUE,
    getAssignedUserDisplayName,
} from "src/modules/data-protection/pages/admin/administration/user-management/tabs-contents/functions/components/functions-table/helpers";

import useAssignableUsers from "../../hooks/useAssignableUsers";

const userUtils = new UserUtils();

const firstNonEmpty = (...values) => {
    for (const value of values) {
        if (typeof value === "string" && value.trim()) {
            return value.trim();
        }
    }

    return "";
};

const getRoleRawValue = (user) => {
    if (!user) {
        return "";
    }

    if (typeof user.role === "string" && user.role.trim()) {
        return user.role.trim();
    }

    if (typeof user.topRole === "string" && user.topRole.trim()) {
        return user.topRole.trim();
    }

    const roles = Array.isArray(user.roles) ? user.roles : [];

    if (roles.length && typeof roles[0] === "string" && roles[0].trim()) {
        return userUtils.getUserTopRole(roles) || roles[0].trim();
    }

    return "";
};

const getAssignedUserPosition = (user) => {
    if (!user) {
        return "";
    }

    const employeeInfo = user.employeeInfo || {};

    return firstNonEmpty(
        user.position,
        user.job_title,
        user.jobTitle,
        employeeInfo.position,
        employeeInfo.job_title,
        employeeInfo.jobTitle
    );
};

const getAssignedUserRoleLabel = (user, t) => {
    const employeeInfo = user?.employeeInfo || {};
    const raw = getRoleRawValue(user) || getRoleRawValue(employeeInfo);

    if (!raw) {
        return "";
    }

    const friendly = userUtils.gerUserRole(raw);

    return t(friendly || raw);
};

const mergeAssignedUser = (assignedUser, source) => {
    if (!assignedUser) {
        return assignedUser;
    }

    if (!source) {
        return assignedUser;
    }

    const employeeInfo =
        assignedUser.employeeInfo || source.employeeInfo || {};

    return {
        ...source,
        ...assignedUser,
        email: assignedUser.email || source.email || employeeInfo.email,
        position: firstNonEmpty(
            assignedUser.position,
            source.position,
            source.job_title,
            source.jobTitle,
            employeeInfo.position,
            employeeInfo.job_title,
            employeeInfo.jobTitle
        ),
        job_title: firstNonEmpty(
            assignedUser.job_title,
            source.job_title,
            employeeInfo.job_title
        ),
        role:
            assignedUser.role ||
            source.role ||
            source.topRole ||
            employeeInfo.role ||
            employeeInfo.topRole,
        roles:
            (Array.isArray(assignedUser.roles) && assignedUser.roles.length
                ? assignedUser.roles
                : null) ||
            source.roles ||
            employeeInfo.roles,
        topRole:
            assignedUser.topRole || source.topRole || employeeInfo.topRole,
        employeeInfo: assignedUser.employeeInfo || source.employeeInfo,
    };
};

const getUserInitials = (user) => {
    const name = getAssignedUserDisplayName(user);

    if (!name) {
        return "";
    }

    const parts = name.split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    return name.slice(0, 2).toUpperCase();
};

const AssignedUserCard = ({ t, user, actions, actionsPlacement = "below" }) => {
    const { users } = useAssignableUsers(t);

    if (!user) {
        return null;
    }

    const source = users.find(
        (item) => String(item.id) === String(user.id)
    );
    const displayUser = mergeAssignedUser(user, source);
    const displayName = getAssignedUserDisplayName(displayUser) || EMPTY_VALUE;
    const position = getAssignedUserPosition(displayUser);
    const roleLabel = getAssignedUserRoleLabel(displayUser, t);
    const actionsEl = actions ? (
        <div className="d-flex flex-wrap gap-2">{actions}</div>
    ) : null;

    return (
        <div>
            <div
                className={`d-flex align-items-start border rounded p-3${
                    actionsPlacement === "inline"
                        ? " justify-content-between flex-wrap gap-2"
                        : ""
                }`}
            >
                <div className="d-flex align-items-start">
                    <div className="avatar-sm me-3">
                        <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                            {getUserInitials(displayUser)}
                        </span>
                    </div>
                    <div>
                        <div>{displayName}</div>
                        {position ? (
                            <div className="text-muted">{position}</div>
                        ) : null}
                        {displayUser.email ? (
                            <div className="text-muted">{displayUser.email}</div>
                        ) : null}
                        {roleLabel ? (
                            <Badge color="soft-primary" className="mt-1">
                                {roleLabel}
                            </Badge>
                        ) : null}
                    </div>
                </div>
                {actionsPlacement === "inline" ? actionsEl : null}
            </div>
            {actionsPlacement === "below" && actionsEl ? (
                <div className="mt-3">{actionsEl}</div>
            ) : null}
        </div>
    );
};

export default withNamespaces()(AssignedUserCard);
